import { FormTemplateUsageResponse } from 'mapistry-shared';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FormTemplateHelpers,
  useEditableFormTemplateState,
} from '../FormTemplateEditor/hooks/useEditableFormTemplateState';
import { EditableFormTemplate } from '../FormTemplateEditor/types';
import { useFormTemplate } from './hooks/useFormTemplate';

type FormTemplateContextType = {
  savedName: string;
  formTemplate: EditableFormTemplate;
  formTemplateUsage?: FormTemplateUsageResponse;
  formTemplateHelpers: FormTemplateHelpers;
  isDirty: boolean;
  isError: boolean;
  isLoading: boolean;
  resetDirtyState: () => void;
};

export const FormTemplateContext = createContext<
  FormTemplateContextType | undefined
>(undefined);

type FormTemplateProviderProps = {
  children: React.ReactNode;
};

export const FormTemplateProvider = ({
  children,
}: FormTemplateProviderProps) => {
  const [isDirty, setIsDirty] = useState(false);
  const { formTemplateSlug, organizationId } = useParams();

  const {
    formTemplate: fetchedFormTemplate,
    isLoading,
    isError,
  } = useFormTemplate({
    slug: formTemplateSlug,
    organizationId,
    config: { useErrorBoundary: true },
  });

  const formTemplateHelpers = useEditableFormTemplateState({
    passedInFormTemplate: fetchedFormTemplate,
    setIsDirty,
  });
  const { formTemplate } = formTemplateHelpers;

  const value = useMemo(
    () => ({
      formTemplate,
      formTemplateUsage: fetchedFormTemplate?.usage,
      formTemplateHelpers,
      isLoading,
      isDirty,
      isError,
      resetDirtyState: () => setIsDirty(false),
      savedName: fetchedFormTemplate?.name || formTemplate?.name,
    }),
    [
      fetchedFormTemplate?.usage,
      formTemplate,
      formTemplateHelpers,
      isLoading,
      isDirty,
      isError,
      fetchedFormTemplate?.name,
    ],
  );

  return (
    <FormTemplateContext.Provider value={value}>
      {children}
    </FormTemplateContext.Provider>
  );
};

export const useFormTemplateContext = () => {
  const context = useContext(FormTemplateContext);
  if (!context) {
    throw new Error(
      'useFormTemplateContext must be used within a FormTemplateProvider',
    );
  }
  return context;
};
